import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button } from 'antd';
import Ellipsis from 'Components/UI/Ellipsis';
import Tooltip from 'Components/UI/Tooltip';
import { GetAvatar, ThumbType } from 'Utils/generateThumbnail';
import Badge from 'Components/UI/Badge';
import { INotificationsMapAux } from 'Data/interfaces/Notification/INotificationsMapAux';
import HighlighterWords from 'Components/UI/HighlightWords';
import { INotificationInAppObraCount } from 'Data/interfaces/Notification/INotification';
import { AvatarWrapperWithTooltip } from 'Components/UI/Tooltip/styles';
import {
  ListSharedUsers,
  ObraAvatar,
  ObraItemWrapper,
  ObraName,
} from './styles';

export interface IObrasItem {
  constructionSiteId: number;
  image?: string;
  thumbSrc?: string;
  thumbType?: ThumbType;
  name: string;
  data: string;
  updateCount?: number;
  sharedUsers?: any[];
  isConnectedApi?: boolean;
  currentPeperUser?: number;
  search: { show: boolean, search?: string };
  notification: INotificationsMapAux[];
  notificationsCount: INotificationInAppObraCount[];
  openUsuariosEPermissoes: () => void;
  openConexaoServico: () => void;
}

const ObrasItem: FC<IObrasItem> = ({
  constructionSiteId,
  image,
  thumbSrc,
  thumbType,
  name,
  data,
  updateCount,
  sharedUsers,
  isConnectedApi,
  currentPeperUser,
  search,
  notification,
  notificationsCount,
  openUsuariosEPermissoes,
  openConexaoServico,
}) => {
  const navigate = useNavigate();

  const valueNotification = useMemo(() => {
    return notificationsCount.find((notificationCount) => {
      return notificationCount.ConstructionSiteId === constructionSiteId;
    })?.Count ?? 0;
  }, [constructionSiteId, notificationsCount]);

  const MAX_NAME_SIZE = 13;

  const openObra = () => {
    if (isConnectedApi) {
      navigate(`/obras/detail/${constructionSiteId}`);
    } else {
      openConexaoServico();
    }
  };

  const displayUsers = () => {
    if (sharedUsers && sharedUsers?.length > 5) {
      const visibleUsers = sharedUsers.slice(0, 4);
      const users = sharedUsers.slice(4);
      const usersList = (
        <ListSharedUsers>
          {users.map((user, index) => (
            <label key={`${user.User.Id}-${index}`}>{user.User.Nome}</label>
          ))}
        </ListSharedUsers>
      );

      return visibleUsers
        .map((sharedUser, index) => (
          <Tooltip
            title={sharedUser.User.Nome}
            placement='bottom'
            key={`${sharedUser.User.Id}-${index}`}
          >
            <AvatarWrapperWithTooltip>
              <GetAvatar
                name={sharedUser.User.Nome}
                shape='circle'
                size={28}
                src={sharedUser.User?.OriginalImageUrl}
                thumbSrc={sharedUser.User?.ThumbnailImageUrl}
                thumbType='small'
              />
            </AvatarWrapperWithTooltip>
          </Tooltip>
        ))
        .concat(
          <Tooltip
            key={`Tooltip-users-${users}`}
            title={usersList}
            placement='bottom'
          >
            <Button shape='circle' type='default' className='buttomListUser'>
              +{users.length}
            </Button>
          </Tooltip>,
        );
    }

    return sharedUsers?.map((sharedUser, index) => (
      <Tooltip
        key={`${sharedUser.User.Id}-${index}`}
        title={sharedUser.User.Nome}
        placement='bottom'
        className={!isConnectedApi ? 'opacitySemConexao' : ''}
      >
        <AvatarWrapperWithTooltip>
          <GetAvatar
            name={sharedUser.User.Nome}
            shape='circle'
            size={28}
            src={sharedUser.User?.OriginalImageUrl}
            thumbSrc={sharedUser.User?.ThumbnailImageUrl}
            thumbType='small'
          />
        </AvatarWrapperWithTooltip>
      </Tooltip>
    ));
  };

  return (
    <ObraItemWrapper>
      <div
        className={(currentPeperUser === 1 || currentPeperUser === 7)
          ? 'obraImg'
          : 'obraImgDisabled'
        }
        onClick={openObra}
        style={{ opacity: !isConnectedApi ? 0.25 : '' }}
      >
        <GetAvatar
          name={name}
          shape='square'
          fontSize={50}
          src={image}
          thumbSrc={thumbSrc}
          thumbType={thumbType}
        />
      </div>

      <ObraName onClick={openObra}>
        {isConnectedApi && (
          <>
            <div className='wrapperNameAndBadge'>
              <strong className='name'>
                <HighlighterWords
                  text={name}
                  maxLength={MAX_NAME_SIZE}
                  wordSearched={!search.search || !search.show ? '' : search.search}
                />
              </strong>
              {valueNotification && valueNotification > 0 ? (
                <Badge type='square' value={valueNotification} />
              ) : null}
            </div>
            <span>Última alteração {data}</span>
          </>
        )}
        {!isConnectedApi && (
          <>
            <strong className='obraNameSemConexao'>
              <Ellipsis phrase={name} maxLength={13} />
              {updateCount && <Badge type='square' value={updateCount} />}
            </strong>
            <span className='semConexao'> Sem conexão</span>
          </>
        )}
      </ObraName>

      <ObraAvatar data-testid='avatares-obra'>
        {displayUsers()}
        <Tooltip
          title='Compartilhar Obra'
          placement='bottom'
          className={!isConnectedApi ? 'opacitySemConexao' : ''}
        >
          {(currentPeperUser === 1 || currentPeperUser === 7) && (
            <Button
              shape='circle'
              className='buttomUser'
              onClick={openUsuariosEPermissoes}
            >
              <label>+</label>
            </Button>
          )}
        </Tooltip>
      </ObraAvatar>
    </ObraItemWrapper>
  );
};

export default ObrasItem;
